import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { usePageMeta, absoluteUrl, SITE_ORIGIN } from "@/lib/usePageMeta";
import { useApi } from "@/lib/api";
import { toStory } from "@/lib/mappers";
import { fmtDate, readTime } from "@/lib/format";
import { StoryImage } from "@/components/site/Media";
import { Markdown } from "@/components/site/Markdown";
import { CategoryTag, MetaRow, PendingAction, EmptyState, ArrowLink, LoadingState, ErrorState } from "@/components/site/Content";
import { StoryCard } from "@/components/site/StoryCard";
import { ShareBar } from "@/components/site/SubscribeButton";
import { useCopy } from "@/lib/copy";

const BackLink = ({ label }) => (
  <Link to="/research" className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline" data-testid="story-back-link">
    <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {label}
  </Link>
);

const InfoRow = ({ label, children }) => (
  <div className="py-4"><dt className="eyebrow text-ink-muted">{label}</dt><dd className="mt-1 font-semibold">{children}</dd></div>
);

const Related = ({ slug, heading }) => {
  const { data } = useApi("/stories", { limit: 4 });
  const related = (data?.items || []).filter((s) => s.slug !== slug).slice(0, 3).map(toStory);
  if (!related.length) return null;
  return (
    <section className="border-t border-line bg-mist" aria-labelledby="related-heading">
      <div className="container-x py-16">
        <h2 id="related-heading" className="mb-8 text-2xl font-extrabold tracking-tight text-brand">{heading}</h2>
        <div className="grid gap-8 md:grid-cols-3">{related.map((s) => <StoryCard key={s.slug} story={s} />)}</div>
      </div>
    </section>
  );
};

export default function StoryDetail() {
  const t = useCopy();
  const { slug } = useParams();
  const { data, loading, error } = useApi(`/stories/${encodeURIComponent(slug)}`);
  const story = data && toStory(data);
  usePageMeta(story ? story.title : loading ? "Loading story" : "Story not found", story?.summary, story && {
    type: "article",
    image: data.coverImageUrl,
    jsonLd: {
      "@context": "https://schema.org", "@type": "NewsArticle", headline: story.title, description: story.summary,
      image: data.coverImageUrl ? [absoluteUrl(data.coverImageUrl)] : undefined, datePublished: data.publicationDate, dateModified: data.updatedAt || data.publicationDate,
      about: ["Bengaluru research", "Bengaluru innovation", data.category].filter(Boolean), mainEntityOfPage: `${SITE_ORIGIN}/research/${data.slug}`,
      publisher: { "@type": "Organization", name: "Bengaluru Research and Innovation Network (BRaIN)", logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/icon-512.png` } },
    },
  });

  if (loading) return <div className="container-x py-20"><LoadingState className="h-[60vh]" label="Loading story" /></div>;
  if (error === "error") return <div className="container-x py-20"><ErrorState testId="story-error" /></div>;
  if (!story) {
    return (
      <div className="container-x py-20">
        <EmptyState title="Story not found" message="This story may have moved or is not yet published." testId="story-not-found" action={<ArrowLink to="/research" testId="story-not-found-link">Browse all stories</ArrowLink>} />
      </div>
    );
  }

  const caption = [story.imageCaption, story.imageCredit && `Image: ${story.imageCredit}`].filter(Boolean).join(" · ");

  return (
    <article data-testid="story-detail">
      <header className="container-x pt-10 md:pt-14">
        <BackLink label={t("stories.detail.back")} />
        <div className="mt-10 max-w-4xl">
          <div className="flex flex-wrap items-center gap-3">
            <CategoryTag>{story.category}</CategoryTag>
          </div>
          <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight text-brand sm:text-5xl lg:text-6xl" data-testid="story-title">{story.title}</h1>
          <MetaRow className="mt-6" items={[{ icon: Calendar, label: fmtDate(story.date) }, { icon: Clock, label: readTime(story.fullContent) }]} />
        </div>
      </header>

      <figure className="container-x mt-10">
        <StoryImage src={story.image} alt={story.imageAlt} testId="story-cover-image" />
        {caption && <figcaption className="mt-3 text-xs text-ink-muted" data-testid="story-image-caption">{caption}</figcaption>}
      </figure>

      <div className="container-x grid gap-12 py-14 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <p className="max-w-[68ch] text-xl font-semibold leading-relaxed text-ink" data-testid="story-intro">{story.summary}</p>
          <Markdown className="mt-8 max-w-[68ch] space-y-6 text-base leading-[1.8] text-ink md:text-lg" testId="story-body">{story.fullContent}</Markdown>
        </div>
        <aside className="space-y-6 lg:col-span-4" aria-label="Story information">
          <dl className="divide-y divide-line border-y border-line" data-testid="story-info">
            {story.institutions?.length > 0 && <InfoRow label={story.institutions.length > 1 ? "Institutions" : "Institution"}>{story.institutions.join(", ")}</InfoRow>}
            {story.domain && <InfoRow label="Research domain">{story.domain}</InfoRow>}
            <InfoRow label="Category">{story.category}</InfoRow>
            {story.technologyReadinessLevel && <InfoRow label="Technology readiness">TRL {story.technologyReadinessLevel} of 9</InfoRow>}
            {story.whoMightBenefit?.length > 0 && (
              <InfoRow label="Who might benefit">
                <ul className="list-disc space-y-1 pl-5">{story.whoMightBenefit.map((w) => <li key={w}>{w}</li>)}</ul>
              </InfoRow>
            )}
          </dl>
          {story.sourceUrl ? (
            <a href={story.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-full bg-brand px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0B3D8A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-testid="story-source-link">{t("stories.detail.source")}<span className="sr-only"> (opens in a new tab)</span></a>
          ) : (
            <PendingAction label="Original source" note="No link to an original source has been provided for this story." testId="story-source-pending" />
          )}
          <ShareBar kind="stories" slug={story.slug} t={t} />
        </aside>
      </div>

      <Related slug={story.slug} heading={t("stories.detail.related")} />
    </article>
  );
}
